import React, { useState } from "react";
import { ArrowLeft, SpeakerHigh, SpeakerSlash, GearSix, Play as PlayIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Letterheist's own screen parts (Jack, 2026-10-25): its header, Settings and
// How to play, shared by the start screen (pages/Letterheist.jsx) and every
// Letterheist game, against the Computer or a player (pages/Game.jsx), so all
// of Letterheist looks the same.

export const LH_PAGE_BG = "#24364f";
export const LH_GREEN = "#2fa05a";

// How to play, in Letterheist's own large print (the ? under the board).
const HOW_TO_PLAY = [
  ["\u{1F3C1}", "The goal", "Make a chain of words from your base (yellow, at the bottom) into the other player's base (blue, at the top)."],
  ["✋", "Your turn", "Tap a letter in your hand, then tap a square. Words read across or down and must join your own words."],
  ["▶", "Play word", "When your words are ready, press Play word. A red tile means that word isn't a real word yet."],
  ["↩", "Take back", "Changed your mind? Take back returns your last tile to your hand."],
  ["\u{1F528}", "Green hammer squares", "Cover one to earn a POW card. Use it to knock one of the other player's tiles off the board."],
  ["■", "Grey walls", "Nobody can put a tile on a grey wall square."],
  ["⏳", "15 turns each", "If nobody gets in by then, whoever is closest to the other base wins."],
];

// Board-game sounds on or off, remembered on this device. The board asks
// for it with lh:ready and hears changes as lh:sound.
export function useLhSound(iframeRef) {
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem("lh:sound") === "off"; } catch { return false; }
  });
  const sendMute = (m) => {
    try { iframeRef.current?.contentWindow?.postMessage({ type: "lh:sound", on: !m }, "*"); } catch { /* ignore */ }
    try { localStorage.setItem("lh:sound", m ? "off" : "on"); } catch { /* ignore */ }
  };
  const toggleSound = () => { setMuted((m) => { const n = !m; sendMute(n); return n; }); };
  return { muted, sendMute, toggleSound };
}

// Back, title, the Settings gear centred between the title and Play word
// (Jack, 2026-10-25), and Play word when there's a game. `extra` sits beside
// the gear (chat, in a game against a player).
export function LhHeader({ onBack, subtitle, onSettings, extra, play }) {
  return (
    <div className="flex items-center gap-2 px-2 h-12 flex-none" style={{ background: "#1b2a44", borderBottom: "1px solid #33496e" }}
      data-testid="lh-header">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="lh-back" className="text-white hover:bg-white/10">
        <ArrowLeft size={18} weight="bold" />
      </Button>
      <div className="min-w-0 leading-tight">
        <div className="font-extrabold text-lg tracking-wide" style={{ color: "#f7d154" }}>Letterheist</div>
        {subtitle && <div className="text-xs font-semibold truncate" style={{ color: "#c9d4e3" }} data-testid="lh-subtitle">{subtitle}</div>}
      </div>
      <span className="flex-1" />
      <Button
        variant="ghost" size="sm" onClick={onSettings}
        aria-label="Settings" title="Settings"
        className="text-white hover:bg-white/10" data-testid="lh-settings"
      >
        <GearSix size={22} weight="bold" />
      </Button>
      {extra}
      <span className="flex-1" />
      {play && (
        <Button
          size="sm"
          className="h-9 px-4 rounded-full text-white font-bold flex-none"
          style={{ background: play.can ? LH_GREEN : "#6b7f94" }}
          disabled={!play.can}
          onClick={play.onClick}
          data-testid="lh-play-btn"
        >
          <PlayIcon size={15} weight="fill" className="mr-1" /> Play word
        </Button>
      )}
    </div>
  );
}

// Board-game sounds. `children` adds game actions (Resign) at the bottom.
export function LhSettingsDialog({ open, onOpenChange, muted, onToggleSound, children }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto border-0"
        style={{ background: "#fffdf4", color: "#15202e" }} data-testid="lh-settings-dialog">
        <DialogTitle className="text-2xl font-extrabold">Settings</DialogTitle>
        <DialogDescription className="text-base" style={{ color: "#5a6577" }}>
          You play the yellow queen and your opponent plays the blue one.
        </DialogDescription>
        <h3 className="text-sm font-bold uppercase tracking-wider mt-1" style={{ color: "#5a6577" }}>Sound</h3>
        <div className="flex items-center justify-between rounded-xl border-2 px-3.5 py-3 text-lg font-bold"
          style={{ background: "#ffffff", borderColor: "#c3ccd8" }}>
          <span className="flex items-center gap-2">
            {muted ? <SpeakerSlash size={20} weight="bold" /> : <SpeakerHigh size={20} weight="bold" />} Board-game sounds
          </span>
          <Switch checked={!muted} onCheckedChange={onToggleSound} aria-label="Board-game sounds" data-testid="lh-sound" />
        </div>
        <Button className="w-full h-12 text-lg font-bold text-white" style={{ background: LH_GREEN }}
          onClick={() => onOpenChange(false)} data-testid="lh-settings-done">Done</Button>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function LhHelpDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto border-0"
        style={{ background: "#fffdf4", color: "#15202e" }} data-testid="lh-help-dialog">
        <DialogTitle className="text-2xl font-extrabold">How to play</DialogTitle>
        <DialogDescription className="sr-only">The rules of Letterheist, step by step.</DialogDescription>
        <div className="space-y-4">
          {HOW_TO_PLAY.map(([icon, title, text]) => (
            <div key={title} className="flex gap-3">
              <div className="flex-none w-11 h-11 rounded-full grid place-items-center text-xl" style={{ background: "#f7d154" }} aria-hidden>{icon}</div>
              <div>
                <div className="text-lg font-extrabold">{title}</div>
                <div className="text-base leading-snug" style={{ color: "#33404f" }}>{text}</div>
              </div>
            </div>
          ))}
        </div>
        <Button className="w-full h-12 text-lg font-bold text-white" style={{ background: LH_GREEN }}
          onClick={() => onOpenChange(false)} data-testid="lh-help-done">Got it</Button>
      </DialogContent>
    </Dialog>
  );
}
